import React from 'react';
import {
  ProgramConfig,
  ProgramPricingVariable,
  PricingVariableVisibility
} from '../../../lib/programConfig';
import { Plus, Trash2, Edit2, Eye, EyeOff, Lock, Check, X } from 'lucide-react';

interface ProgramPricingVariablesEditorProps {
  config: ProgramConfig;
  onChange: (updated: ProgramConfig) => void;
}

export default function ProgramPricingVariablesEditor({
  config,
  onChange,
}: ProgramPricingVariablesEditorProps) {
  const variables = config.pricingVariables || [];

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [formName, setFormName] = React.useState('');
  const [formDescription, setFormDescription] = React.useState('');
  const [formVisibility, setFormVisibility] = React.useState<PricingVariableVisibility>('visible');
  const [formCode, setFormCode] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setFormVisibility('visible');
    setFormCode('');
    setErrorMsg(null);
  };

  const startEdit = (varItem: ProgramPricingVariable) => {
    setEditingId(varItem.id);
    setFormName(varItem.name);
    setFormDescription(varItem.description || '');
    setFormVisibility(varItem.visibility);
    setFormCode(varItem.code || '');
    setErrorMsg(null);
  };

  const handleSave = () => {
    setErrorMsg(null);
    if (!formName.trim()) {
      setErrorMsg('Variable Name is required.');
      return;
    }

    let nextCode = formCode.trim();
    // Check code uniqueness across variables if code is supplied
    if (nextCode) {
      if (!/^\d{5}$/.test(nextCode)) {
        setErrorMsg('Variable Code must be a 5-digit number (e.g. 10014).');
        return;
      }
      const duplicateCode = variables.find(
        (v) => v.code === nextCode && v.id !== editingId
      );
      if (duplicateCode) {
        setErrorMsg(`Variable Code '${nextCode}' is already used by another variable.`);
        return;
      }
    }

    const now = new Date().toISOString();

    if (editingId) {
      // Update existing
      const updatedVars = variables.map((v) => {
        if (v.id === editingId) {
          return {
            ...v,
            name: formName.trim(),
            description: formDescription.trim() || undefined,
            visibility: formVisibility,
            code: nextCode || v.code,
            updatedAt: now,
          };
        }
        return v;
      });
      onChange({ ...config, pricingVariables: updatedVars });
    } else {
      // Create new
      const newVar: ProgramPricingVariable = {
        id: `var_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        code: nextCode, // If blank, backend/server will auto-generate 5-digit code upon save
        visibility: formVisibility,
        currency: 'USD',
        sortOrder: variables.length,
        createdAt: now,
        updatedAt: now,
      };
      onChange({ ...config, pricingVariables: [...variables, newVar] });
    }

    resetForm();
  };

  const handleDelete = (id: string) => {
    const updatedVars = variables.filter((v) => v.id !== id);
    onChange({ ...config, pricingVariables: updatedVars });
    if (editingId === id) resetForm();
  };

  const toggleVisibility = (varItem: ProgramPricingVariable) => {
    const nextVis: PricingVariableVisibility =
      varItem.visibility === 'visible'
        ? 'hidden'
        : varItem.visibility === 'hidden'
        ? 'disabled'
        : 'visible';

    const updatedVars = variables.map((v) =>
      v.id === varItem.id ? { ...v, visibility: nextVis, updatedAt: new Date().toISOString() } : v
    );
    onChange({ ...config, pricingVariables: updatedVars });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-slate-900">Program Pricing Variables</h3>
        <p className="text-xs text-slate-500 mt-1">
          Configure pricing options (e.g. 4 Months vs 6 Months). Applicants must select an eligible variable when applying.
          Assigning unique 5-digit codes keeps variables linked across invoices, quotes, batches, and workflow records.
        </p>
      </div>

      {errorMsg && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
          {errorMsg}
        </div>
      )}

      {/* Add / Edit Form Box */}
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
            {editingId ? 'Edit Pricing Variable' : 'Add New Pricing Variable'}
          </span>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Cancel Edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Variable Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Work and Travel (4 Months)"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Description
            </label>
            <input
              type="text"
              placeholder="e.g. Standard 4-month program intake duration"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              5-Digit Code (Optional - Auto generated)
            </label>
            <input
              type="text"
              maxLength={5}
              placeholder="Auto e.g. 10014"
              value={formCode}
              onChange={(e) => setFormCode(e.target.value.replace(/\D/g, ''))}
              className="w-full px-3 py-1.5 text-xs font-mono bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Visibility Status
            </label>
            <select
              value={formVisibility}
              onChange={(e) => setFormVisibility(e.target.value as PricingVariableVisibility)}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-primary"
            >
              <option value="visible">Visible (Available for new apps)</option>
              <option value="hidden">Hidden (Hidden from new apps)</option>
              <option value="disabled">Disabled (Completely unavailable)</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={handleSave}
              className="w-full py-1.5 px-3 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              {editingId ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {editingId ? 'Update Variable' : 'Add Variable'}
            </button>
          </div>
        </div>
      </div>

      {/* Variables List Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
            <tr>
              <th className="py-2.5 px-3">Code</th>
              <th className="py-2.5 px-3">Variable Name</th>
              <th className="py-2.5 px-3">Description</th>
              <th className="py-2.5 px-3 text-center">Visibility</th>
              <th className="py-2.5 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {variables.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-slate-500 italic">
                  No pricing variables configured yet. Add one above.
                </td>
              </tr>
            ) : (
              variables.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="py-2.5 px-3 font-mono font-medium text-slate-700">
                    {v.code ? (
                      <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-800 font-semibold">
                        {v.code}
                      </span>
                    ) : (
                      <span className="text-slate-500 italic">Pending...</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">{v.name}</td>
                  <td className="py-2.5 px-3 text-slate-600">
                    {v.description || <span className="text-slate-500 italic">—</span>}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleVisibility(v)}
                      title="Click to cycle visibility status"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors"
                    >
                      {v.visibility === 'visible' && (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Eye className="w-3 h-3" /> Visible
                        </span>
                      )}
                      {v.visibility === 'hidden' && (
                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Hidden
                        </span>
                      )}
                      {v.visibility === 'disabled' && (
                        <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Disabled
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="py-2.5 px-3 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => startEdit(v)}
                      className="p-1 text-slate-600 hover:text-indigo-600 rounded transition-colors"
                      title="Edit Variable"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(v.id)}
                      className="p-1 text-slate-400 hover:text-red-600 rounded transition-colors"
                      title="Delete Variable"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
